// Partner visits API (2026-10-06): see docs/superpowers/specs/2026-10-06-partner-visits-api-design.md

import { observable, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

// Platform-admin management of OAuth clients (approved AI scribe vendors).
// Field edits on an existing client go through LocalModel.saveField -> PATCH
// /api/oauth/clients/:id; creation and secret rotation are explicit calls.
// Spec: docs/superpowers/specs/2026-10-06-partner-visits-api-design.md §6.
export class OauthClientAdminStore extends LocalModel {
    @observable oauthClients = [];

    @observable client = {};

    @observable newClient = {};

    @observable revealedSecret = '';

    @observable error = '';

    @observable loading = false;

    // Platform-admin grant overview (2026-10-21): active grants across all users.
    @observable grants = [];

    @observable grantsLoading = false;

    // MCP usage log (2026-10-29): who uses which client and tool.
    @observable usageSummary = [];

    @observable usageDays = 30;

    @observable usageItems = [];

    @observable usageTotal = 0;

    @observable usageLoading = false;

    // MCP usage insights (2026-10-30): charts data for the usage page.
    @observable usageInsights = null;

    @observable usageInsightsLoading = false;

    // Every tool the hosted endpoint can expose, so the usage page also lists unused ones (2026-10-29).
    @observable toolCatalog = [];

    constructor() {
        super('oauthClient', {
            namePlural: 'oauthClients',
            api: {
                search: { url: '/api/oauth/clients/', params: {} },
                load: { url: '/api/oauth/clients/', params: {} },
                save: { url: '/api/oauth/clients/', params: {} },
                delete: { url: '/api/oauth/clients/', params: {} },
            },
        });
    }

    @action
    selectClient(client) {
        this.client = client ? { ...client } : {};
        this.revealedSecret = '';
        this.error = '';
    }

    @action
    clearSecret() {
        this.revealedSecret = '';
    }

    async loadClients() {
        this.updateKeyValue('loading', true);
        const response = await util.fetchApi('/api/oauth/clients', { method: 'GET' });
        if (response && response.status === 200) {
            this.updateKeyValue('oauthClients', Array.isArray(response.data) ? response.data : []);
            this.updateKeyValue('error', '');
        } else {
            this.updateKeyValue('error', (response && response.message) || 'Could not load clients');
        }
        this.updateKeyValue('loading', false);
    }

    async createClient() {
        const response = await util.fetchApi('/api/oauth/clients', { method: 'POST' }, { ...this.newClient });
        if (response && response.status === 201 && response.data) {
            this.updateKeyValue('revealedSecret', response.data.clientSecret || '');
            this.updateKeyValue('newClient', {});
            this.updateKeyValue('error', '');
            await this.loadClients();
            return true;
        }
        this.updateKeyValue('error', (response && response.message) || 'Could not create client');
        return false;
    }

    async loadGrants(clientId = '') {
        this.updateKeyValue('grantsLoading', true);
        const params = clientId ? { clientId } : {};
        const response = await util.fetchApi('/api/oauth/admin/grants', { method: 'GET' }, params);
        if (response && response.status === 200 && response.data) {
            const items = Array.isArray(response.data.items) ? response.data.items : [];
            this.updateKeyValue('grants', items);
            this.updateKeyValue('error', '');
        } else {
            this.updateKeyValue('grants', []);
            this.updateKeyValue('error', (response && response.message) || 'Could not load connections');
        }
        this.updateKeyValue('grantsLoading', false);
    }

    async revokeGrant(id, clientId = '') {
        const response = await util.fetchApi(`/api/oauth/admin/grants/${id}`, { method: 'DELETE' });
        if (response && response.status === 200) {
            this.updateKeyValue('error', '');
            await this.loadGrants(clientId);
            return true;
        }
        this.updateKeyValue('error', (response && response.message) || 'Could not revoke connection');
        return false;
    }

    async loadUsageSummary(params = {}) {
        this.updateKeyValue('usageLoading', true);
        const response = await util.fetchApi('/api/oauth/admin/usage-summary', { method: 'GET' }, params);
        if (response && response.status === 200 && response.data) {
            const items = Array.isArray(response.data.items) ? response.data.items : [];
            this.updateKeyValue('usageSummary', items);
            this.updateKeyValue('usageDays', response.data.days || params.days || 30);
            this.updateKeyValue('error', '');
        } else {
            this.updateKeyValue('usageSummary', []);
            this.updateKeyValue('error', (response && response.message) || 'Could not load usage');
        }
        this.updateKeyValue('usageLoading', false);
    }

    async loadUsageInsights(params = {}) {
        this.updateKeyValue('usageInsightsLoading', true);
        const response = await util.fetchApi('/api/oauth/admin/usage-insights', { method: 'GET' }, params);
        if (response && response.status === 200 && response.data) {
            this.updateKeyValue('usageInsights', response.data);
            this.updateKeyValue('error', '');
        } else {
            this.updateKeyValue('usageInsights', null);
            this.updateKeyValue('error', (response && response.message) || 'Could not load usage insights');
        }
        this.updateKeyValue('usageInsightsLoading', false);
    }

    async loadToolCatalog() {
        const response = await util.fetchApi('/api/oauth/admin/tool-catalog', { method: 'GET' }, {});
        if (response && response.status === 200 && response.data) {
            const items = Array.isArray(response.data.items) ? response.data.items : [];
            this.updateKeyValue('toolCatalog', items);
        } else {
            this.updateKeyValue('toolCatalog', []);
        }
    }

    async loadUsage(params = {}) {
        this.updateKeyValue('usageLoading', true);
        const response = await util.fetchApi('/api/oauth/admin/usage', { method: 'GET' }, params);
        if (response && response.status === 200 && response.data) {
            const items = Array.isArray(response.data.items) ? response.data.items : [];
            this.updateKeyValue('usageItems', items);
            this.updateKeyValue('usageTotal', response.data.total || 0);
            this.updateKeyValue('error', '');
        } else {
            this.updateKeyValue('usageItems', []);
            this.updateKeyValue('usageTotal', 0);
            this.updateKeyValue('error', (response && response.message) || 'Could not load usage');
        }
        this.updateKeyValue('usageLoading', false);
    }

    async rotateSecret(id) {
        const response = await util.fetchApi(`/api/oauth/clients/${id}/rotate-secret`, { method: 'POST' });
        if (response && response.status === 200 && response.data) {
            this.updateKeyValue('revealedSecret', response.data.clientSecret || '');
            this.updateKeyValue('error', '');
            return true;
        }
        this.updateKeyValue('error', (response && response.message) || 'Could not rotate secret');
        return false;
    }
}

const oauthClientAdminStore = new OauthClientAdminStore();
export default oauthClientAdminStore;
